import React, { useState, useEffect } from 'react';
import {
  Box, Card, Table, TableBody, TableCell, TableContainer, TableHead,
  TableRow, IconButton, Chip, Typography, Dialog, DialogTitle,
  DialogContent, DialogActions, Button, TextField
} from '@mui/material';
import { CheckCircle, Cancel, Star, StarBorder, Visibility } from '@mui/icons-material';
import { getTemoignages, moderateTemoignage } from '../services/api';

function TemoignagesPage() {
  const [temoignages, setTemoignages] = useState([]);
  const [viewDialog, setViewDialog] = useState({ open: false, temoignage: null });
  const [moderateDialog, setModerateDialog] = useState({ open: false, temoignage: null, action: '' });
  const [note, setNote] = useState('');

  useEffect(() => { loadTemoignages(); }, []);

  const loadTemoignages = async () => {
    try {
      const { data } = await getTemoignages();
      setTemoignages(data);
    } catch (error) {
      console.error('Erreur:', error);
    }
  };

  const handleModerate = async () => {
    try {
      await moderateTemoignage(moderateDialog.temoignage._id, {
        status: moderateDialog.action,
        moderationNote: note
      });
      setModerateDialog({ open: false, temoignage: null, action: '' });
      setNote('');
      loadTemoignages();
    } catch (error) {
      console.error('Erreur:', error);
    }
  };

  const handleToggleFeatured = async (temoignage) => {
    try {
      await moderateTemoignage(temoignage._id, { isFeatured: !temoignage.isFeatured, status: temoignage.status });
      loadTemoignages();
    } catch (error) {
      console.error('Erreur:', error);
    }
  };

  const statusColors = { pending: 'warning', approved: 'success', rejected: 'error' };
  const statusLabels = { pending: 'En attente', approved: 'Approuve', rejected: 'Rejete' };
  const typeLabels = { AL: 'Amylose AL', 'ATTR-h': 'ATTR Hereditaire', 'ATTR-wt': 'ATTR Sauvage', AA: 'Amylose AA', autre: 'Autre' };

  return (
    <Box>
      <Typography variant="h4" fontWeight="bold" mb={3}>Temoignages</Typography>

      <Card>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Patient</TableCell>
                <TableCell>Type</TableCell>
                <TableCell>Titre</TableCell>
                <TableCell>Statut</TableCell>
                <TableCell>Date</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {temoignages.map((t) => (
                <TableRow key={t._id} hover>
                  <TableCell>
                    <Typography fontWeight="medium">{t.firstName}</Typography>
                    <Typography variant="body2" color="text.secondary">{t.age} ans - {t.region}</Typography>
                  </TableCell>
                  <TableCell>{typeLabels[t.amyloseType] || t.amyloseType}</TableCell>
                  <TableCell>{t.title}</TableCell>
                  <TableCell><Chip size="small" label={statusLabels[t.status]} color={statusColors[t.status]} /></TableCell>
                  <TableCell>{new Date(t.createdAt).toLocaleDateString('fr-FR')}</TableCell>
                  <TableCell align="right">
                    <IconButton size="small" onClick={() => handleToggleFeatured(t)}>
                      {t.isFeatured ? <Star color="warning" /> : <StarBorder />}
                    </IconButton>
                    <IconButton size="small" onClick={() => setViewDialog({ open: true, temoignage: t })}>
                      <Visibility />
                    </IconButton>
                    {t.status === 'pending' && (
                      <>
                        <IconButton size="small" color="success" onClick={() => setModerateDialog({ open: true, temoignage: t, action: 'approved' })}>
                          <CheckCircle />
                        </IconButton>
                        <IconButton size="small" color="error" onClick={() => setModerateDialog({ open: true, temoignage: t, action: 'rejected' })}>
                          <Cancel />
                        </IconButton>
                      </>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Card>

      {/* View Dialog */}
      <Dialog open={viewDialog.open} onClose={() => setViewDialog({ open: false, temoignage: null })} maxWidth="md" fullWidth>
        <DialogTitle>{viewDialog.temoignage?.title}</DialogTitle>
        <DialogContent>
          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
            {viewDialog.temoignage?.firstName}, {viewDialog.temoignage?.age} ans - {typeLabels[viewDialog.temoignage?.amyloseType]}
          </Typography>
          <Typography variant="h6" mt={2}>Histoire</Typography>
          <Typography paragraph>{viewDialog.temoignage?.story}</Typography>
          {viewDialog.temoignage?.diagnosisJourney && (
            <>
              <Typography variant="h6">Parcours diagnostic</Typography>
              <Typography paragraph>{viewDialog.temoignage?.diagnosisJourney}</Typography>
            </>
          )}
          {viewDialog.temoignage?.advice && (
            <>
              <Typography variant="h6">Conseil</Typography>
              <Typography paragraph>{viewDialog.temoignage?.advice}</Typography>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setViewDialog({ open: false, temoignage: null })}>Fermer</Button>
        </DialogActions>
      </Dialog>

      {/* Moderate Dialog */}
      <Dialog open={moderateDialog.open} onClose={() => setModerateDialog({ open: false, temoignage: null, action: '' })}>
        <DialogTitle>{moderateDialog.action === 'approved' ? 'Approuver' : 'Rejeter'} le temoignage</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="Note de moderation (optionnel)"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            margin="normal"
            multiline
            rows={2}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setModerateDialog({ open: false, temoignage: null, action: '' })}>Annuler</Button>
          <Button variant="contained" color={moderateDialog.action === 'approved' ? 'success' : 'error'} onClick={handleModerate}>
            {moderateDialog.action === 'approved' ? 'Approuver' : 'Rejeter'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default TemoignagesPage;
